import { combineReducers } from 'redux';
import thunk from 'redux-thunk';
import { persistStore, persistReducer } from 'redux-persist';
import localforage from 'localforage';
import { configureStore } from '@reduxjs/toolkit';
import { loggerMiddleware } from './loggerMiddleware';

import registrationReducer from './reducers/registration';
import {
  advanceDashboardReducer,
  agingReducer,
  announcementReducer,
  authenticationReducer,
  bizMetaReducer,
  bizStateReducer,
  cashPositionReducer,
  customReportReducer,
  daloyReducer,
  dashboardReducer,
  fsReducer,
  glReducer,
  globalStateReducer,
  imbalanceReducer,
  jePtsReducer,
  jePtsSummaryReducer,
  matureChecksReducer,
  orderSummaryReducer,
  postingSummaryReducer,
  preferencesReducer,
  privilegesReducer,
  purchaseSummaryReducer,
  purchaseReturnSummaryReducer,
  registryReducer,
  salesSummaryReducer,
  salesReturnSummaryReducer,
  setupReducer,
  scReducer,
  slReducer,
  slSubBalancesReducer,
  advSLSubBalancesReducer,
  sopReducer,
  storedValuesReducer,
  sosReducer,
  specialJournalsReducer,
  transactionReducer,
  wfAdminReducer,
  wfAssignedReducer,
  envReducer,
  budgetRAODReducer,
  budgetRAPALReducer,
  budgetSummaryReducer,
  budgetCOReducer,
  wfStatsReducer,
  budgetLedgerReducer,
  portalReducer,
  sl2Reducer,
  endingInventoryReducer,
  cashierLogsReducer,
  prAnnualSummaryReducer,
  trtnMonthPayReducer,
  jvTransReducer,
  gjReducer,
  saaoReducer,
  whTransferReducer,
  brTransferReducer,
  checkRegisterReducer,
  cashflowLedgerReducer,
  stockImageReducer,
  sl3Reducer,
  wfReducer,
  socketReducer,
  chatReducer,
  snLogsReducer,
  subCacheReducer,
  portalStoreReducer
} from './slices';

const rootReducer = combineReducers({
  advanceDashboard: advanceDashboardReducer,
  aging: agingReducer,
  announcement: announcementReducer,
  auth: authenticationReducer,
  bizMeta: bizMetaReducer,
  bizState: bizStateReducer,
  budgetLedger: budgetLedgerReducer,
  budgetRAOD: budgetRAODReducer,
  budgetRAPAL: budgetRAPALReducer,
  budgetSummary: budgetSummaryReducer,
  budgetCO: budgetCOReducer,
  cashPosition: cashPositionReducer,
  customReport: customReportReducer,
  daloy: daloyReducer,
  dashboard: dashboardReducer,
  endingInventory: endingInventoryReducer,
  snLogs: snLogsReducer,
  env: envReducer,
  fs: fsReducer,
  gl: glReducer,
  global: globalStateReducer,
  imbal: imbalanceReducer,
  jepts: jePtsReducer,
  jeptssummary: jePtsSummaryReducer,
  matureChecks: matureChecksReducer,
  orderSummary: orderSummaryReducer,
  portal: portalReducer,
  postingSummary: postingSummaryReducer,
  preferences: preferencesReducer,
  prAnnualSummary: prAnnualSummaryReducer,
  privileges: privilegesReducer,
  purchaseSummary: purchaseSummaryReducer,
  purchaseReturnSummary: purchaseReturnSummaryReducer,
  registrationReducer,
  registry: registryReducer,
  salesSummary: salesSummaryReducer,
  salesReturnSummary: salesReturnSummaryReducer,
  setup: setupReducer,
  sc: scReducer,
  sl: slReducer,
  sl2: sl2Reducer,
  sl3: sl3Reducer,
  slSubBalances: slSubBalancesReducer,
  advSLSubBalances: advSLSubBalancesReducer,
  sop: sopReducer,
  storedValues: storedValuesReducer,
  sos: sosReducer,
  specialJournals: specialJournalsReducer,
  transactions: transactionReducer,
  trtnMonthPay: trtnMonthPayReducer,
  wfAdmin: wfAdminReducer,
  wfAssigned: wfAssignedReducer,
  wfStats: wfStatsReducer,
  cashierLogs: cashierLogsReducer,
  jvTrans: jvTransReducer,
  gj: gjReducer,
  saao: saaoReducer,
  whTransfer: whTransferReducer,
  brTransfer: brTransferReducer,
  checkRegister: checkRegisterReducer,
  cashflowLedger: cashflowLedgerReducer,
  stockImage: stockImageReducer,
  wf: wfReducer,
  portalStore: portalStoreReducer,
  socketio: socketReducer,
  chat: chatReducer,
  subCache: subCacheReducer
});

const IS_DEV = process.env.NODE_ENV === 'development';
if (IS_DEV) {
  const enabled = localStorage.getItem('REDUX_LOGGER') === 'true';

  console.info(
    `%cRedux Logger: ${enabled ? 'ENABLED' : 'DISABLED'}`,
    `color: ${enabled ? 'limegreen' : 'orange'}; font-weight: bold;`
  );

  console.info(`
🔒 Redux logger is OFF by default.

To enable it, run:
  localStorage.setItem('REDUX_LOGGER', 'true')
  location.reload()

To disable it again:
  localStorage.setItem('REDUX_LOGGER', 'false')
  location.reload()
`);
}
// enable logging
// localStorage.setItem('REDUX_LOGGER', 'true');  // enable
// localStorage.setItem('REDUX_LOGGER', 'false'); // disable
// location.reload();

const PURGE_ON_REHYDRATE = ['wfAssigned', 'wf', 'snLogs', 'portalStore'];

const persistConfig = {
  key: 'root',
  storage: localforage,
  blacklist: PURGE_ON_REHYDRATE,
  migrate: async state => {
    if (!state) return state;

    const cleaned = { ...state };

    for (const key of PURGE_ON_REHYDRATE) {
      if (cleaned[key] || key in cleaned) {
        delete cleaned[key];
      }
    }

    return cleaned;
  }
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const isLoggerEnabled = () => localStorage.getItem('REDUX_LOGGER') === 'true';

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware => {
    const middleware = getDefaultMiddleware({
      thunk: true,
      serializableCheck: false,
      immutableCheck: false
    });

    if (IS_DEV && isLoggerEnabled()) {
      middleware.push(loggerMiddleware);
    }

    return middleware;
  }
});

export const persistor = persistStore(store);
